import React, { useState } from 'react';
import { HeroSection } from '../../components/HeroSection';
import { Button } from '../../components/ui/Button';
import { motion } from 'framer-motion';
import { Clock, Users, Video, BookOpen, Calendar, Filter } from 'lucide-react';
export function Courses() {
  const [filter, setFilter] = useState<'all' | 'cohort' | 'self-paced' | 'workshop'>('all');
  const courses = [{
    title: 'Unlearning Anti-Fat Bias 101',
    type: 'cohort',
    duration: '6 weeks',
    format: 'Live sessions + async work',
    participants: '15-20 people',
    status: 'Enrolling Now',
    price: '$200-500 sliding scale',
    description: 'A foundational course exploring the roots of fatphobia, how it shows up in daily life, and how to begin unlearning internalized anti-fatness.',
    topics: ['History of fat activism', 'Internalized fatphobia', 'Medical gaslighting', 'Setting boundaries', 'Fat joy practices'],
    nextStart: 'March 15, 2024'
  }, {
    title: 'History of Fat Activism',
    type: 'self-paced',
    duration: '8 hours',
    format: 'Video series + readings',
    participants: 'Unlimited',
    status: 'Available Now',
    price: '$75',
    description: 'Trace the lineage of fat liberation from the 1960s to today. Understand the shoulders we stand on and the debates that shaped the movement.',
    topics: ['The Fat Underground', 'Disability justice connections', 'HAES origins', 'Contemporary voices', 'Global perspectives'],
    nextStart: 'Start anytime'
  }, {
    title: 'Medical Advocacy for Fat Folks',
    type: 'workshop',
    duration: '3 hours',
    format: 'Interactive workshop',
    participants: '30 max',
    status: 'Next Session: April 2024',
    price: '$50-150 sliding scale',
    description: 'Practical tools and scripts for navigating the healthcare system while fat. Led by fat-friendly healthcare providers.',
    topics: ['Preparing for appointments', 'Advocating for yourself', 'Finding fat-friendly providers', 'Knowing your rights', 'Documentation strategies'],
    nextStart: 'April 20, 2024'
  }, {
    title: 'Fat Liberation for Educators',
    type: 'cohort',
    duration: '4 weeks',
    format: 'Live sessions + curriculum development',
    participants: '12-15 educators',
    status: 'Coming Soon',
    price: '$300-600 sliding scale',
    description: 'For teachers, professors, and education workers who want to create fat-inclusive classrooms and challenge diet culture in schools.',
    topics: ['Classroom environment', 'Curriculum audit', 'Responding to bullying', 'Parent communication', 'Self-care for fat educators'],
    nextStart: 'Summer 2024'
  }, {
    title: 'Reading Fat Liberation Theory',
    type: 'cohort',
    duration: '8 weeks',
    format: 'Discussion-based',
    participants: '10-12 people',
    status: 'Enrolling Now',
    price: '$150-400 sliding scale',
    description: 'Deep dive into key texts in fat studies, disability justice, and anti-oppression theory. For people who love to read and discuss ideas.',
    topics: ['Sabrina Strings', "Da'Shaun Harrison", 'Sonya Renee Taylor', 'Aubrey Gordon', 'Academic fat studies'],
    nextStart: 'March 22, 2024'
  }, {
    title: 'Fat & Disabled: Navigating Dual Identities',
    type: 'workshop',
    duration: '2 hours',
    format: 'Panel + Q&A',
    participants: '50 max',
    status: 'Next Session: May 2024',
    price: '$25-75 sliding scale',
    description: 'Fat disabled people share their experiences navigating medical systems, accessibility, and the intersection of fatphobia and ableism.',
    topics: ['Medical model vs social model', 'Access needs', 'Mobility aids', 'Workplace accommodations', 'Community care'],
    nextStart: 'May 10, 2024'
  }];
  const filteredCourses = filter === 'all' ? courses : courses.filter(course => course.type === filter);
  const filterButtons = [{
    label: 'All Courses',
    value: 'all' as const
  }, {
    label: 'Cohort-Based',
    value: 'cohort' as const
  }, {
    label: 'Self-Paced',
    value: 'self-paced' as const
  }, {
    label: 'Workshops',
    value: 'workshop' as const
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-725">
      <HeroSection title="Courses & Learning Experiences" subtitle="Justice-led education, not compliance-driven training." tag="Learning & Growth" bgColor="cyan" textColor="navy" data-id="element-726" />

      {/* Filter Bar */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto border-b-2 border-gray-100 -mt-8 bg-white relative z-10 shadow-lg" data-id="element-727">
        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-728">
          <div className="flex items-center space-x-2" data-id="element-729">
            <Filter className="h-5 w-5 text-brand-cyan" data-id="element-730" />
            <span className="font-bold text-sm uppercase" data-id="element-731">Filter:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-732">
            {filterButtons.map(btn => <button key={btn.value} onClick={() => setFilter(btn.value)} className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all border-2 ${filter === btn.value ? 'bg-brand-cyan text-brand-navy border-brand-cyan' : 'bg-white text-gray-600 border-gray-200 hover:border-brand-cyan'}`} data-id="element-733">
                {btn.label}
              </button>)}
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-734">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-735">
          {filteredCourses.map((course, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="border-2 border-brand-black bg-white flex flex-col" data-id="element-736">
              {/* Header */}
              <div className="bg-brand-cyan/10 p-6 border-b-2 border-brand-black" data-id="element-737">
                <div className="flex items-start justify-between mb-4" data-id="element-738">
                  <div className="flex-1" data-id="element-739">
                    <h3 className="text-2xl font-black uppercase text-brand-black mb-2" data-id="element-740">
                      {course.title}
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-741">
                      <span className="px-3 py-1 bg-brand-cyan text-brand-navy text-xs font-bold uppercase border border-brand-cyan" data-id="element-742">
                        {course.type}
                      </span>
                      <span className={`px-3 py-1 text-xs font-bold uppercase border ${course.status.includes('Now') ? 'bg-brand-lime/20 text-brand-lime border-brand-lime' : 'bg-gray-100 text-gray-600 border-gray-300'}`} data-id="element-743">
                        {course.status}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-gray-700 leading-relaxed" data-id="element-744">
                  {course.description}
                </p>
              </div>

              {/* Details */}
              <div className="p-6 flex-grow" data-id="element-745">
                <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-746">
                  <div className="flex items-start space-x-3" data-id="element-747">
                    <Clock className="h-5 w-5 text-brand-cyan flex-shrink-0 mt-0.5" data-id="element-748" />
                    <div data-id="element-749">
                      <p className="text-xs font-bold uppercase text-gray-500" data-id="element-750">
                        Duration
                      </p>
                      <p className="text-sm font-medium" data-id="element-751">{course.duration}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3" data-id="element-752">
                    <Users className="h-5 w-5 text-brand-cyan flex-shrink-0 mt-0.5" data-id="element-753" />
                    <div data-id="element-754">
                      <p className="text-xs font-bold uppercase text-gray-500" data-id="element-755">
                        Participants
                      </p>
                      <p className="text-sm font-medium" data-id="element-756">
                        {course.participants}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3" data-id="element-757">
                    <Video className="h-5 w-5 text-brand-cyan flex-shrink-0 mt-0.5" data-id="element-758" />
                    <div data-id="element-759">
                      <p className="text-xs font-bold uppercase text-gray-500" data-id="element-760">
                        Format
                      </p>
                      <p className="text-sm font-medium" data-id="element-761">{course.format}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3" data-id="element-762">
                    <Calendar className="h-5 w-5 text-brand-cyan flex-shrink-0 mt-0.5" data-id="element-763" />
                    <div data-id="element-764">
                      <p className="text-xs font-bold uppercase text-gray-500" data-id="element-765">
                        Next Start
                      </p>
                      <p className="text-sm font-medium" data-id="element-766">{course.nextStart}</p>
                    </div>
                  </div>
                </div>

                {/* Topics */}
                <div className="mb-6" data-id="element-767">
                  <p className="text-xs font-bold uppercase text-gray-500 mb-3 flex items-center" data-id="element-768">
                    <BookOpen className="h-4 w-4 mr-2 text-brand-cyan" data-id="element-769" />
                    What You'll Learn
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-770">
                    {course.topics.map((topic, i) => <span key={i} className="text-xs bg-gray-100 px-3 py-1 border border-gray-200" data-id="element-771">
                        {topic}
                      </span>)}
                  </div>
                </div>

                {/* Price & CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100" data-id="element-772">
                  <div data-id="element-773">
                    <p className="text-xs font-bold uppercase text-gray-500" data-id="element-774">
                      Investment
                    </p>
                    <p className="text-lg font-black text-brand-cyan" data-id="element-775">
                      {course.price}
                    </p>
                  </div>
                  <Button color="cyan" size="sm" disabled={course.status === 'Coming Soon'} data-id="element-776">
                    {course.status.includes('Now') ? 'Enroll Now' : 'Learn More'}
                  </Button>
                </div>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Community Scholarships */}
      <section className="py-16 bg-brand-navy text-white" data-id="element-777">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-778">
          <h2 className="text-3xl font-black uppercase mb-6" data-id="element-779">
            Community Scholarships Available
          </h2>
          <p className="text-lg mb-8 opacity-90" data-id="element-780">
            We offer sliding scale pricing and full scholarships for all
            courses. No one is turned away for lack of funds.
          </p>
          <Button color="cyan" size="lg" data-id="element-781">
            Apply for Scholarship
          </Button>
        </div>
      </section>
    </div>;
}